import type { ComponentProps } from 'react';
import React, { useId } from 'react';
import styled from '@emotion/styled';
import type { AuthenticationProvider } from 'graphql-ts-types';
import type {
  BorderRadiusProps,
  MarginBottomProps,
  MarginTopProps,
  PaddingProps,
  ResponsiveValue,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import { borderRadius, space, width } from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { ButtonKind } from '../Button';
import { Button } from '../Button';
import type { ButtonWithIconProps } from '../Button/ButtonWithIcon';
import ButtonWithIcon, { DEFAULT_ICON_SIZE } from '../Button/ButtonWithIcon';
import { HeadlineH8 } from '../Typography';
import VisuallyHidden from '../VisuallyHidden';
import SubmitButtonAnimation from './SubmitButtonAnimation';

interface FormSubmitButtonProps
  extends
    Omit<ComponentProps<typeof Button>, 'kind'>,
    MarginTopProps,
    MarginBottomProps,
    PaddingProps,
    WidthProps,
    Partial<ButtonWithIconProps> {
  defaultButtonLabel: string;
  successButtonLabel?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSuccess?: boolean;
  isRounded?: boolean;
  kind?: Omit<ButtonKind, 'green'>;
  successButtonSize?: ResponsiveValue<number>;
  eventName?: string;
  trackingParams?: {
    provider: AuthenticationProvider;
    [key: string]: string;
  };
  form?: string;
  ariaLabel?: string;
}

const SPINNER_SIZE = [20, null, null, 22];

const buttonStyles = {
  width: 1,
  mb: 'sp_16',
};

const StyledButton = styled(Button)<WidthProps & SpaceProps & BorderRadiusProps>`
  ${width};
  ${space};
  ${borderRadius};
`;

const getButtonType = ({ kind, isSuccess }: Pick<FormSubmitButtonProps, 'kind' | 'isSuccess'>) => {
  if (isSuccess) {
    return 'green';
  }

  return (kind ?? 'primary') as ButtonKind;
};

// eslint-disable-next-line complexity
const SubmitButton = ({
  defaultButtonLabel,
  successButtonLabel,
  isDisabled = false,
  isLoading,
  isSuccess,
  isRounded,
  iconName,
  iconSize = DEFAULT_ICON_SIZE,
  iconGap,
  kind,
  successButtonSize,
  eventName,
  trackingParams,
  ...rest
}: FormSubmitButtonProps) => {
  const { t } = useLocalization();
  const disabledReasonId = useId();
  const Component = iconName ? ButtonWithIcon : StyledButton;

  return (
    <>
      <Component
        type="button"
        {...buttonStyles}
        {...rest}
        kind={getButtonType({ kind, isSuccess })}
        disabled={isDisabled || isLoading}
        isLoading={isLoading}
        {...(!isSuccess ? { iconName } : {})}
        iconSize={iconSize}
        spinnerSize={SPINNER_SIZE}
        iconGap={iconGap}
        {...(isRounded ? { borderRadius: 'x-large' } : {})}
        {...(eventName
          ? {
              tracking: {
                eventName,
                text: defaultButtonLabel,
                ...trackingParams,
              },
            }
          : {})}
        {...(isDisabled && !isSuccess
          ? {
              'aria-describedby': disabledReasonId,
            }
          : {})}
      >
        <HeadlineH8 as="span" color="inherit">
          {isSuccess && successButtonLabel ? (
            <SubmitButtonAnimation {...(successButtonSize ? { iconSize: successButtonSize } : {})}>
              {successButtonLabel}
            </SubmitButtonAnimation>
          ) : (
            defaultButtonLabel
          )}
        </HeadlineH8>
      </Component>
      {isDisabled && !isSuccess && (
        <VisuallyHidden as="span" id={disabledReasonId}>
          {t('h24_fill_form_fields')}
        </VisuallyHidden>
      )}
    </>
  );
};

const FormSubmitButton = (props: FormSubmitButtonProps) => (
  <SubmitButton type="submit" {...props} />
);

export { SubmitButton };
export default FormSubmitButton;
